
import React, { useRef } from "react";
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import Modal from '../../components/ui/Modal';
import api from '../../api/api';
import { getSessionUsername } from '../../utils/sessionUser';

const SalesOrderItems = ({ orderId, customerId, orderDate, items, user, setError, refetchData }) => {
  const [availableItems, setAvailableItems] = React.useState([]);
  const [selectedItemId, setSelectedItemId] = React.useState('');
  const [rate, setRate] = React.useState('');
  const [quantity, setQuantity] = React.useState('');
  const [discountPer, setDiscountPer] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const itemNameRef = useRef(null);
  const [itemSearch, setItemSearch] = React.useState('');
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [filteredItems, setFilteredItems] = React.useState([]);
  const [stockData, setStockData] = React.useState([]);
  const [cpid, setCpid] = React.useState(0);
  const [vrid, setVrid] = React.useState(0);
  const qtyRef = useRef(null);
  const [taxTypes, setTaxTypes] = React.useState([]);
  const [selectedTaxGroupId, setSelectedTaxGroupId] = React.useState('');
  const [remarks, setRemarks] = React.useState('');
  const [isItemDropdownOpen, setIsItemDropdownOpen] = React.useState(false);
  const [isTaxDropdownOpen, setIsTaxDropdownOpen] = React.useState(false);
  const [focusRights, setFocusRights] = React.useState({});
  const addBtnRef = useRef(null); 
  const itemOptionRefs = useRef([]);
  const [customerName, setCustomerName] = React.useState('');

const [highlightIndex, setHighlightIndex] = React.useState(-1);
const [taxHighlightIndex, setTaxHighlightIndex] = React.useState(-1);

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [editItemId, setEditItemId] = React.useState(null);

  const fieldSequence = [
  { id: "itemNameField", label: "Item Name", ref: itemNameRef },
  { id: "qtyField", label: "Quantity", ref: qtyRef },
  { id: "rateField", label: "Rate" },
  { id: "discField", label: "Disc %" },
  { id: "taxTypeField", label: "Tax Type" },
  { id: "remarksField", label: "Remarks" },
  { id: "addItemBtn", label: "Save Button" },
];

  const [editItemData, setEditItemData] = React.useState(null); // store raw item object while editing
  const getDiscPer = (item) => {
    if (!item) return 0;
    const keys = ["DiscPer", "discper", "Disc%", "Discount", "discount", "DiscountPer", "discountper", "DISC_PER"];
    for (const key of keys) {
      if (item[key] !== undefined && item[key] !== null) {
        return Number(item[key]) || 0;
      }
    }
    return 0;
  };

const focusNext = (currentLabel) => {
  const idx = fieldSequence.findIndex(f => f.label === currentLabel);
  if (idx === -1) return;

  for (let i = idx + 1; i < fieldSequence.length; i++) {
    const f = fieldSequence[i];
    if (canFocus(f.label)) {
      document.getElementById(f.id)?.focus();
      return;
    }
  }
};

React.useEffect(() => {
  if (isModalOpen) {
    api.getFocusRightsAPI(user?.UsrName, "SalesOrder")
      .then(res => {
        const rights = {};
        (res.Table || []).forEach(r => {
          rights[r.ColumnNames?.trim()] = r.IsFocus;
        });
        setFocusRights(rights);
      })
      .catch(err => console.error("Focus rights error:", err));
  }
}, [isModalOpen, user]);

const canFocus = (label) => {
  return focusRights[label] !== 0; 
};

const handleDecimalChange = (setter) => (event) => {
  const value = event.target.value;
  if (/^\d*\.?\d*$/.test(value)) {
    setter(value);
  }
};

  React.useEffect(() => {
    if (!isModalOpen) {
      setTimeout(() => addBtnRef.current?.focus(), 50);
    }
  }, [isModalOpen]);

  React.useEffect(() => {
    if (!itemSearch.trim()) {
      setFilteredItems(availableItems);
    } else {
      const search = itemSearch.toLowerCase();
      const filtered = availableItems.filter(item =>
        item.ItemNo?.toLowerCase().includes(search) ||
        item.barcode?.toLowerCase().includes(search)
      );
      setFilteredItems(filtered);
    }
  }, [itemSearch, availableItems]);

  React.useEffect(() => {
  if (isModalOpen) {
    setTimeout(() => {
      itemNameRef.current?.focus();
    }, 50);
  }
}, [isModalOpen]);


  React.useEffect(() => {
    api.getSelectedSalesOrder(orderId)
      .then(data => {
        const normalized = (data.Table1 || []).map(i => ({
          ...i,
          Itemid: i.Itemid ?? i.ItemId ?? 0,
          Remarks: i.Remarks ?? i.Notes ?? "",
        }));
        setAvailableItems(normalized);

        if (data.Table && data.Table.length > 0) {
          setCpid(data.Table[0].cpid || 0);
          setVrid(data.Table[0].Vrid || 0);
          setCustomerName(data.Table[0].vname || "N/A"); 
        }

        if (data.Table4) {
          setTaxTypes(data.Table4);
        }
      })
      .catch(() => setError("Failed to load available items."));
  }, [orderId, setError]);

  React.useEffect(() => {
    const match = availableItems.find(
      i => i.ItemNo?.toLowerCase() === itemSearch.toLowerCase() ||
        i.barcode?.toLowerCase() === itemSearch.toLowerCase()
    );
    if (match) setSelectedItemId(match.ItemId);
  }, [itemSearch, availableItems]);

  React.useEffect(() => {
    if (isItemDropdownOpen && highlightIndex >= 0) {
      itemOptionRefs.current[highlightIndex]?.scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightIndex, isItemDropdownOpen]);

  const handleItemSelect = (itemId, action = null) => {
  const item = availableItems.find(i => i.Itemid === parseInt(itemId));
  if (item) {
    setSelectedItemId(item.Itemid);
    setRate(item.Rate);
    setDiscountPer(getDiscPer(item));
    setItemSearch(item.ItemNo);
    fetchStockData(item.Itemid);
  }

  setIsItemDropdownOpen(false);
  setHighlightIndex(-1);

  // If we are editing or adding and pressed Enter, go next
  if (action === "next") {
    setTimeout(() => {
      if (canFocus("Quantity")) {
        qtyRef.current?.focus();
      } else {
        focusNext("Item Name");
      }
    }, 50);
  }
};



  const fetchStockData = async (itemId) => {
    try {
      const res = await api.StockQtyItemWiseTaxAPI(itemId);
      setStockData(res?.Table || []);
    } catch (err) {
      console.error("Stock fetch error:", err);
      setStockData([]);
    }
  };

   // 🔹 Derived amounts
const qtyNum = parseFloat(quantity) || 0;
const rateNum = parseFloat(rate) || 0;
const discNum = parseFloat(discountPer) || 0;

const baseAmount = qtyNum * rateNum;
const discAmount = baseAmount * (discNum / 100);   // NEW
const subtotal = baseAmount - discAmount;          // after discount

const selTax = taxTypes.find(t => t.GroupId == selectedTaxGroupId);
const taxPer = selTax ? parseFloat(selTax.TaxPer) || 0 : 0;
const taxAmount = (subtotal * taxPer) / 100;

const netTotal = subtotal + taxAmount;
const taxTypeName = selTax?.TaxtType || "";

  const handleSaveItem = async (stayOpen = false) => {
    let itemId = selectedItemId;

    if (!editItemId && !itemId) {
      const match = availableItems.find(
        i =>
          i.ItemNo?.trim().toLowerCase() === itemSearch.trim().toLowerCase() ||
          i.barcode?.trim() === itemSearch.trim()
      );
      if (match) {
        itemId = match.Itemid ?? match.ItemId;
      } else {
        alert("Please select an item and enter quantity, rate, and discount.");
        return;
      }
    }

    if (qtyNum <= 0 || rateNum <= 0) {
      alert("Quantity and Rate must be greater than 0.");
      return;
    }

  const payload = {
  Itemid: itemId,
  SessionId: "9944010155",
  Vid: customerId,
  User: user?.UsrName || getSessionUsername(),
  OrderDate: new Date(orderDate).toISOString().split("T")[0],
  Qty: qtyNum,
  Rate: rateNum,
  Amount: baseAmount,
  Notes: remarks,              // already present
  ThisPoId: orderId,
  DiscPer: discNum,
  Seqid: editItemId || 0,
  TaxPer: taxPer,
  TaxType: taxTypeName,
  Subtotal: subtotal,
  TaxTotal: taxAmount,
  NetAmount: netTotal,
  Remarks: remarks,           
};

    setLoading(true);
    try {
      await api.MobSaveOrderDetailsRevTaxAPI(payload);
      await refetchData();
      resetForm();

      if (!stayOpen) {
        setIsModalOpen(false);
      } else {
        setTimeout(() => itemNameRef.current?.focus(), 50);
      }
    } catch (err) {
      console.error("Save error:", err);
      setError("Failed to save item.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    setLoading(true);
    try {
      await api.deleteSalesOrderItem(orderId, itemId);
      await refetchData();
    } catch (err) {
      console.error("Delete error:", err.response || err);
      setError("Failed to delete item.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditItem = (item) => {
  const seq = item.Seqid ?? item.seqid ?? 0;
  const id = item.Itemid ?? item.ItemId ?? 0;
  console.log("🟢 Editing Item:", item);

  setEditItemId(seq);
  setEditItemData(item); 

  setSelectedItemId(id);
  setItemSearch(item.ItemNo ?? item.Partno ?? "");
  setQuantity(String(item.Qty ?? item.qty ?? ""));
  setRate(String(item.Rate ?? item.rate ?? ""));
  setDiscountPer(String(getDiscPer(item)));
  setRemarks(item.Remarks ?? "");

  setIsModalOpen(true);

  if (id) {
    fetchStockData(id);
  }

  // ✅ Ensure focus is reset to first field
  setTimeout(() => itemNameRef.current?.focus(), 50);
};

    React.useEffect(() => {
      if (!isModalOpen || !editItemData || taxTypes.length === 0) return;
  
      const item = editItemData;
  
     
      let matchedGroupId = item.TaxGroupId ?? item.Taxgroupid ?? item.TaxGroup ?? null;
  
      
      if (!matchedGroupId) {
        const itemTaxTypeText = (item.TaxType || item.TaxtType || item.TaxTypeName || '')?.toString().trim().toLowerCase();
        if (itemTaxTypeText) {
          matchedGroupId = (taxTypes.find(t => (t.TaxtType || t.TaxType || '').toString().trim().toLowerCase() === itemTaxTypeText) || {}).GroupId;
        }
      }
  
     
      if (!matchedGroupId) {
        const itemTaxPer = Number(item.TaxPer ?? item.taxper ?? 0);
        if (itemTaxPer) {
          matchedGroupId = (taxTypes.find(t => Number(t.TaxPer) === itemTaxPer) || {}).GroupId;
        }
      }
  
     
      if (!matchedGroupId) {
        matchedGroupId = taxTypes[0].GroupId;
        console.log("⚠️ Item has no TaxGroupId or match; defaulting to first:", matchedGroupId);
      } else {
        console.log("✅ Restored TaxGroupId from item match:", matchedGroupId);
      }
  
      setSelectedTaxGroupId(matchedGroupId);
    }, [isModalOpen, editItemData, taxTypes]);
  
    const resetForm = () => {
      setEditItemId(null);
      setEditItemData(null);
      setSelectedItemId('');
      setQuantity('');
      setRate('');
      setDiscountPer('');
      setItemSearch('');
      setSelectedTaxGroupId(taxTypes[0]?.GroupId ?? '');
      setRemarks('');
    };
    
  const totalAmount = items.reduce(
    (sum, item) => sum + ((item.Rate || 0) * (item.Qty || 0) * (1 - (getDiscPer(item) / 100))),
    0
  );

  return (
    <div className="w-full">
      <div className="mb-2 text-right">
         <Button
          ref={addBtnRef} // ✅ Assign ref
          onClick={() => { resetForm(); setIsModalOpen(true); }}
          className="px-2 py-1 bg-blue-500 text-white"
        >
          + Add
        </Button>
      </div>

      {/* Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}
        title={editItemId ? "Edit Item" : "Add New Sales Item"}
        heightClass="h-[65vh]">
          
  {/* Customer Info Row */}
  <div className="mb-4 p-2 border-b border-gray-200">
    <div className="text-lg font-semibold">
      Customer: {customerName || "N/A"}
    </div>  
  </div>
        <div className="grid items-end grid-cols-1 gap-2 sm:grid-cols-5">
      {/* Item Search / Item Name */}
<div className="relative sm:col-span-2">
  <label className="block text-sm font-medium">Item Name*</label>
  <input
    id="itemNameField"
    ref={itemNameRef}
    type="text"
    value={itemSearch}
    onChange={(e) => {
      setItemSearch(e.target.value);
      setIsItemDropdownOpen(true);
    }}
    onFocus={() => {
      setIsItemDropdownOpen(true);
      if (!itemSearch) setFilteredItems(availableItems);
    }}
    onKeyDown={(e) => {
      if (isItemDropdownOpen && filteredItems.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setHighlightIndex((prev) => (prev + 1) % filteredItems.length);
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setHighlightIndex((prev) => (prev - 1 + filteredItems.length) % filteredItems.length);
        } else if (e.key === "Enter") {
          e.preventDefault();
          const chosen =
            highlightIndex >= 0 ? filteredItems[highlightIndex] : filteredItems[0];
          if (chosen) {
            handleItemSelect(chosen.Itemid, "next"); // pass flag to move next
          }
        }
      } else if (e.key === "Enter") {
        // If dropdown is not open, just move to Quantity
        e.preventDefault();
        focusNext("Item Name");
      }
    }}
    className="w-full px-2 py-1 border rounded"
  />

  {/* Dropdown */}
  {isItemDropdownOpen && filteredItems.length > 0 && (
    <ul className="absolute z-10 w-full mt-1 bg-white border rounded shadow max-h-48 overflow-y-auto">
      {filteredItems.map((item, idx) => (
        <li
          key={item.Itemid}
          ref={(el) => {
            itemOptionRefs.current[idx] = el;
          }}
          className={`px-2 py-1 cursor-pointer ${
            idx === highlightIndex ? "bg-blue-100" : ""
          }`}
          onMouseDown={() => handleItemSelect(item.Itemid, "next")}
        >
          {item.ItemNo} - {item.ItemName}
        </li>
      ))}
    </ul>
  )}
</div>


          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium">Quantity*</label>
<input
  id="qtyField"
  ref={qtyRef}
  type="text"
  inputMode="decimal"
  value={quantity}
  onChange={handleDecimalChange(setQuantity)}
  onKeyDown={(e) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      focusNext("Quantity");
    }
  }}
  tabIndex={canFocus("Quantity") ? 2 : -1} className="w-full border rounded px-2 py-1 bg-gray-100"
/>

          </div>

          {/* Rate */}
          <div>
            <label className="block text-sm font-medium">Rate*</label>
<input
  id="rateField"
  type="text"
  inputMode="decimal"
  value={rate}
  onChange={handleDecimalChange(setRate)}
  onKeyDown={(e) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      focusNext("Rate");
    }
  }}
  tabIndex={canFocus("Rate") ? 3 : -1} className="w-full border rounded px-2 py-1 bg-gray-100"
/>

          </div>

          {/* Discount % */}
          <div>
            <label className="block text-sm font-medium">Disc %</label>

<input
  id="discField"
  type="text"
  inputMode="decimal"
  value={discountPer}
  onChange={handleDecimalChange(setDiscountPer)}
  onKeyDown={(e) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      focusNext("Disc %");
    }
  }}
  tabIndex={canFocus("Disc %") ? 4 : -1} className="w-full border rounded px-2 py-1 bg-gray-100"
/>

          </div>

        </div>

        {/* Discount Amount (calculated) */}
        <div className="grid items-end grid-cols-1 gap-2 sm:grid-cols-5">
          <div>
            <label className="block text-sm font-medium">Disc Amt</label>
            <input
              type="text"
              value={discAmount.toFixed(2)}
              readOnly
              tabIndex={-1}   // skip on Tab
              className="w-full border rounded px-2 py-1 bg-gray-100"
            />
          </div>

        {/* Tax Type */}
<div className="relative">
  <label className="block text-sm font-medium">Tax Type*</label>
  <div
    id="taxTypeField"
    tabIndex={5}
    className="w-full border rounded px-2 py-1 cursor-pointer bg-white"
    onClick={() => {
      setIsTaxDropdownOpen(prev => !prev);
      setTaxHighlightIndex(-1);
    }}
    onFocus={() => {
      setIsTaxDropdownOpen(true);
      setTaxHighlightIndex(-1);
    }}
    onKeyDown={(e) => {
      if (isTaxDropdownOpen && taxTypes.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setTaxHighlightIndex((prev) => (prev + 1) % taxTypes.length);
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setTaxHighlightIndex((prev) => (prev - 1 + taxTypes.length) % taxTypes.length);
        } else if (e.key === "Enter" || e.key === "Tab") {
          e.preventDefault();
          const chosen =
            taxHighlightIndex >= 0 ? taxTypes[taxHighlightIndex] : taxTypes[0];
          if (chosen) setSelectedTaxGroupId(chosen.GroupId);
          setIsTaxDropdownOpen(false);
          setTimeout(() => document.getElementById("remarksField")?.focus(), 50);
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        setIsTaxDropdownOpen(true);
      }
    }}
  >
    {(() => {
      const sel = taxTypes.find(t => t.GroupId == selectedTaxGroupId);
      return sel ? sel.TaxtType : "-- Select Tax --";
    })()}
  </div>

  {/* ✅ Dropdown list */}
  {isTaxDropdownOpen && taxTypes.length > 0 && (
    <ul className="absolute z-10 w-full mt-1 bg-white border rounded shadow max-h-40 overflow-y-auto">
      {taxTypes.map((tax, idx) => (
        <li
          key={tax.GroupId}
          onClick={() => {
            setSelectedTaxGroupId(tax.GroupId);
            setIsTaxDropdownOpen(false);
            setTimeout(() => document.getElementById("remarksField")?.focus(), 50);
          }}
          className={`px-2 py-1 cursor-pointer ${
            idx === taxHighlightIndex ? "bg-blue-200" : "hover:bg-gray-100"
          }`}
        >
          {tax.TaxtType}
        </li>
      ))}
    </ul>
  )}
</div>

        </div>
        {/* 🔹 Calculated values */}
        <div className="sm:col-span-5 mt-3 grid grid-cols-3 gap-2 text-sm">
          <div className="p-2 border rounded bg-gray-50" tabIndex={-1}>
            <span className="font-medium">Amount:</span> {subtotal.toFixed(2)}
          </div>
          <div className="p-2 border rounded bg-gray-50" tabIndex={-1}>
            <span className="font-medium">Tax Amt:</span> {taxAmount.toFixed(2)}
          </div>
          <div className="p-2 border rounded bg-gray-100 font-bold" tabIndex={-1}>
            <span className="font-medium">Net Total:</span> {netTotal.toFixed(2)}
          </div>
        </div>


        {/* Remarks */}
        <div>
          <label className="block text-sm font-medium">Remarks</label>
          <input
            id="remarksField"
            type="text"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                handleSaveItem(true);   // call directly instead of button click
              }
            }}
            tabIndex={6}
            className="w-full border rounded px-2 py-1"
          />

   </div>

       {/* Save Button */}
<div className="mt-4 text-right">
<Button
  id="addItemBtn"
  onClick={() => handleSaveItem(true)}  // stay open for next item
  disabled={loading || cpid > 0 || vrid > 0}
  tabIndex={7}
  className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
>
  {loading ? "Saving..." : editItemId ? "Update Item" : "Add Item"}
</Button>
</div>

        {stockData.length > 0 && (
          <div className="mt-3 sm:col-span-5">
            <h4 className="font-semibold text-sm mb-2 text-gray-700">
              Stock Availability
            </h4>
            <div className="overflow-hidden rounded-md border border-gray-200 shadow-sm">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-2 py-1 text-right font-medium">StkQty</th>
                    <th className="px-2 py-1 text-left font-medium">Godown</th>
                  </tr>
                </thead>
                <tbody>
                  {stockData.map((row, idx) => {
                    let qtyClass =
                      row.StkQty < 0
                        ? "bg-red-100 text-red-700"
                        : row.StkQty === 0
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-green-100 text-green-700";

                    return (
                      <tr
                        key={idx}
                        className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td
                          className={`px-2 py-1 text-right rounded-sm ${qtyClass}`}
                        >
                          {row.StkQty}
                        </td>
                        <td className="px-2 py-1">{row.GdName}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>

      {/* Items Table */}
     <h3 className="mt-4 mb-2 text-lg font-semibold">Sales Order Items</h3>
<table className="w-full text-sm border">
  <thead className="bg-gray-50">
    <tr>
      <th className="px-2 py-2 text-left">Item</th>
      <th className="px-2 py-2 text-right">Qty</th>
      <th className="px-2 py-2 text-right">Rate</th>
      <th className="px-2 py-2 text-right">Disc %</th>
      <th className="px-2 py-2 text-right">Disc Amt</th>
      <th className="px-2 py-2 text-right">Subtotal</th>  
      <th className="px-2 py-2 text-right">Tax Amt</th>    
      <th className="px-2 py-2 text-right">Net Total</th> 
      <th className="px-2 py-2 text-center">Action</th>
    </tr>
  </thead>
  <tbody>
    {items.map((item, idx) => {
      const id = item.Seqid ?? item.seqid ?? item.Itemid ?? item.ItemId ?? idx;
      const itemName = item.Partno ?? item.ItemNo ?? "Unnamed";
      const qty = Number(item.Qty ?? 0);
      const rate = Number(item.Rate ?? 0);
      const disc = getDiscPer(item);

      // --- calculations ---
      const subtotal = qty * rate;   // before discount
      const discAmount = subtotal * (disc / 100);
      const baseAmount = subtotal - discAmount;
      const taxRow = taxTypes.find(t => t.GroupId == (item.TaxGroupId ?? 0));
      const taxPer = taxRow?.TaxPer ?? 0;
      const taxAmt = item.TaxTotal !== undefined 
  ? Number(item.TaxTotal) 
  : baseAmount * (taxPer / 100);
      const netTotal = baseAmount + taxAmt;

      return (
        <tr key={id} className="border-t">
          <td className="px-2 py-1">{itemName}</td>
          <td className="px-2 py-1 text-right">{qty}</td>
          <td className="px-2 py-1 text-right">{rate.toFixed(2)}</td>
          <td className="px-2 py-1 text-right">{disc}</td>
          <td className="px-2 py-1 text-right">{discAmount.toFixed(2)}</td>
          <td className="px-2 py-1 text-right">{baseAmount.toFixed(2)}</td> {/* subtotal */}
          <td className="px-2 py-1 text-right">{taxAmt.toFixed(2)}</td>
          <td className="px-2 py-1 text-right">{netTotal.toFixed(2)}</td>
          <td className="px-2 py-1 text-center space-x-2">
            <Button
              onClick={() => handleEditItem(item)}
              className="px-2 py-1 text-xs bg-yellow-500 hover:bg-yellow-600"
            >
              Edit
            </Button>
            <Button
              onClick={() => handleDeleteItem(id)}
              className="px-2 py-1 text-xs bg-red-500 hover:bg-red-600"
            >
              Delete
            </Button>
          </td>
        </tr>
      );
    })}
  </tbody>
 <tfoot>
  <tr className="font-bold bg-gray-50 text-xl">
    {/* Left: Item count */}
    <td className="p-2 text-left font-bold">No. of Items: {items.length}</td>

    {/* Keep the existing columns exactly as is */}
    <td colSpan="7" className="p-2 text-right font-bold">Total Net Amount</td>
    <td className="p-2 text-right">
      {items
        .reduce((sum, item) => {
          const qty = Number(item.Qty ?? 0);
          const rate = Number(item.Rate ?? 0);
          const disc = getDiscPer(item);
          const subtotal = qty * rate;
          const baseAmount = subtotal - subtotal * (disc / 100);
          const taxRow = taxTypes.find(t => t.GroupId == (item.TaxGroupId ?? 0));
          const taxPer = taxRow?.TaxPer ?? 0;
          const taxAmt =
            item.TaxTotal !== undefined ? Number(item.TaxTotal) : baseAmount * (taxPer / 100);
          return sum + baseAmount + taxAmt;
        }, 0)
        .toLocaleString("en-IN", { style: "currency", currency: "INR" })}
    </td>
    <td></td>
  </tr>
</tfoot>

</table>

    </div>
  
  );
};

export default SalesOrderItems;





// import React, { useRef } from "react";
// import Button from '../../components/ui/Button';
// import Spinner from '../../components/ui/Spinner';
// import Modal from '../../components/ui/Modal';
// import api from '../../api/api';

// const SalesOrderItems = ({ orderId, customerId, orderDate, items, user, setError, refetchData }) => {
//   const [availableItems, setAvailableItems] = React.useState([]);
//   const [selectedItemId, setSelectedItemId] = React.useState('');
//   const [rate, setRate] = React.useState('');
//   const [quantity, setQuantity] = React.useState('');
//   const [discountPer, setDiscountPer] = React.useState('');
//   const [loading, setLoading] = React.useState(false);
//   const itemNameRef = useRef(null);
//   const [itemSearch, setItemSearch] = React.useState('');
//   const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
//   const [filteredItems, setFilteredItems] = React.useState([]);
//   const [stockData, setStockData] = React.useState([]);
//   const [cpid, setCpid] = React.useState(0);
//   const [vrid, setVrid] = React.useState(0);
//   const qtyRef = useRef(null);
//   const [taxTypes, setTaxTypes] = React.useState([]);
//   const [selectedTaxGroupId, setSelectedTaxGroupId] = React.useState('');
//   const [remarks, setRemarks] = React.useState('');
//   const [isItemDropdownOpen, setIsItemDropdownOpen] = React.useState(false);
//   const [isTaxDropdownOpen, setIsTaxDropdownOpen] = React.useState(false);
//   const [focusRights, setFocusRights] = React.useState({});
//   const addBtnRef = useRef(null); 

// const [highlightIndex, setHighlightIndex] = React.useState(-1);
// const [taxHighlightIndex, setTaxHighlightIndex] = React.useState(-1);

//   const [isModalOpen, setIsModalOpen] = React.useState(false);
//   const [editItemId, setEditItemId] = React.useState(null);

//   const fieldSequence = [
//   { id: "itemNameField", label: "Item Name", ref: itemNameRef },
//   { id: "qtyField", label: "Quantity", ref: qtyRef },
//   { id: "rateField", label: "Rate" },
//   { id: "discField", label: "Disc %" },
//   { id: "taxTypeField", label: "Tax Type" },
//   { id: "remarksField", label: "Remarks" },
//   { id: "addItemBtn", label: "Save Button" },
// ];

//   const [editItemData, setEditItemData] = React.useState(null); // store raw item object while editing
//   const getDiscPer = (item) => {
//     if (!item) return 0;
//     const keys = ["DiscPer", "discper", "Disc%", "Discount", "discount", "DiscountPer", "discountper", "DISC_PER"];
//     for (const key of keys) {
//       if (item[key] !== undefined && item[key] !== null) {
//         return Number(item[key]) || 0;
//       }
//     }
//     return 0;
//   };

// const focusNext = (currentLabel) => {
//   const idx = fieldSequence.findIndex(f => f.label === currentLabel);
//   if (idx === -1) return;

//   for (let i = idx + 1; i < fieldSequence.length; i++) {
//     const f = fieldSequence[i];
//     if (canFocus(f.label)) {
//       document.getElementById(f.id)?.focus();
//       return;
//     }
//   }
// };

// React.useEffect(() => {
//   if (isModalOpen) {
//     api.getFocusRightsAPI(user?.UsrName, "SalesOrder")
//       .then(res => {
//         const rights = {};
//         (res.Table || []).forEach(r => {
//           rights[r.ColumnNames?.trim()] = r.IsFocus;
//         });
//         setFocusRights(rights);
//       })
//       .catch(err => console.error("Focus rights error:", err));
//   }
// }, [isModalOpen, user]);

// const canFocus = (label) => {
//   return focusRights[label] !== 0; 
// };

//   React.useEffect(() => {
//     if (!isModalOpen) {
//       setTimeout(() => addBtnRef.current?.focus(), 50);
//     }
//   }, [isModalOpen]);

//   React.useEffect(() => {
//     if (!itemSearch.trim()) {
//       setFilteredItems(availableItems);
//     } else {
//       const search = itemSearch.toLowerCase();
//       const filtered = availableItems.filter(item =>
//         item.ItemNo?.toLowerCase().includes(search) ||
//         item.barcode?.toLowerCase().includes(search)
//       );
//       setFilteredItems(filtered);
//     }
//   }, [itemSearch, availableItems]);

//   React.useEffect(() => {
//   if (isModalOpen) {
//     setTimeout(() => {
//       itemNameRef.current?.focus();
//     }, 50);
//   }
// }, [isModalOpen]);


//   React.useEffect(() => {
//     api.getSelectedSalesOrder(orderId)
//       .then(data => {
//         const normalized = (data.Table1 || []).map(i => ({
//           ...i,
//           Itemid: i.Itemid ?? i.ItemId ?? 0,
//           Remarks: i.Remarks ?? i.Notes ?? "",
//         }));
//         setAvailableItems(normalized);

//         if (data.Table && data.Table.length > 0) {
//           setCpid(data.Table[0].cpid || 0);
//           setVrid(data.Table[0].Vrid || 0);
//         }

//         if (data.Table4) {
//           setTaxTypes(data.Table4);
//         }
//       })
//       .catch(() => setError("Failed to load available items."));
//   }, [orderId, setError]);

//   React.useEffect(() => {
//     const match = availableItems.find(
//       i => i.ItemNo?.toLowerCase() === itemSearch.toLowerCase() ||
//         i.barcode?.toLowerCase() === itemSearch.toLowerCase()
//     );
//     if (match) setSelectedItemId(match.ItemId);
//   }, [itemSearch, availableItems]);

//   const handleItemSelect = (itemId) => {
//     const item = availableItems.find(i => i.Itemid === parseInt(itemId));
//     if (item) {
//       setSelectedItemId(item.Itemid);
//       setRate(item.Rate);
//       setDiscountPer(getDiscPer(item));
//       setItemSearch(item.ItemNo);
//       fetchStockData(item.Itemid);
//     }
// // Inside handleItemSelect
// setIsDropdownOpen(false);
// setTimeout(() => {
//   if (canFocus("Quantity")) {
//     qtyRef.current?.focus();
//   } else {
//     focusNext("Item Name");
//   }
// }, 50);

//   };

//   const fetchStockData = async (itemId) => {
//     try {
//       const res = await api.StockQtyItemWiseTaxAPI(itemId);
//       setStockData(res?.Table || []);
//     } catch (err) {
//       console.error("Stock fetch error:", err);
//       setStockData([]);
//     }
//   };

//    // 🔹 Derived amounts
// const qtyNum = parseFloat(quantity) || 0;
// const rateNum = parseFloat(rate) || 0;
// const discNum = parseFloat(discountPer) || 0;

// const baseAmount = qtyNum * rateNum;
// const discAmount = baseAmount * (discNum / 100);   // NEW
// const subtotal = baseAmount - discAmount;          // after discount

// const selTax = taxTypes.find(t => t.GroupId == selectedTaxGroupId);
// const taxPer = selTax ? parseFloat(selTax.TaxPer) || 0 : 0;
// const taxAmount = (subtotal * taxPer) / 100;

// const netTotal = subtotal + taxAmount;
// const taxTypeName = selTax?.TaxtType || "";

//   const handleSaveItem = async (stayOpen = false) => {
//     let itemId = selectedItemId;

//     if (!editItemId && !itemId) {
//       const match = availableItems.find(
//         i =>
//           i.ItemNo?.trim().toLowerCase() === itemSearch.trim().toLowerCase() ||
//           i.barcode?.trim() === itemSearch.trim()
//       );
//       if (match) {
//         itemId = match.Itemid ?? match.ItemId;
//       } else {
//         setError("Please select an item and enter quantity, rate, and discount.");
//         return;
//       }
//     }

//     if (qtyNum <= 0 || rateNum <= 0) {
//       setError("Quantity and Rate must be greater than 0.");
//       return;
//     }

//   const payload = {
//   Itemid: itemId,
//   SessionId: "9944010155",
//   Vid: customerId,
//   User: user?.UsrName || "admin",
//   OrderDate: new Date(orderDate).toISOString().split("T")[0],
//   Qty: qtyNum,
//   Rate: rateNum,
//   Amount: baseAmount,
//   Notes: remarks,              // already present
//   ThisPoId: orderId,
//   DiscPer: discNum,
//   Seqid: editItemId || 0,
//   TaxPer: taxPer,
//   TaxType: taxTypeName,
//   Subtotal: subtotal,
//   TaxTotal: taxAmount,
//   NetAmount: netTotal,
//   Remarks: remarks,           
// };

//     setLoading(true);
//     try {
//       await api.MobSaveOrderDetailsRevTaxAPI(payload);
//       await refetchData();
//       resetForm();

//       if (!stayOpen) {
//         setIsModalOpen(false);
//       } else {
//         setTimeout(() => itemNameRef.current?.focus(), 50);
//       }
//     } catch (err) {
//       console.error("Save error:", err);
//       setError("Failed to save item.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleDeleteItem = async (itemId) => {
//     setLoading(true);
//     try {
//       await api.deleteSalesOrderItem(orderId, itemId);
//       await refetchData();
//     } catch (err) {
//       console.error("Delete error:", err.response || err);
//       setError("Failed to delete item.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleEditItem = (item) => {
//   const seq = item.Seqid ?? item.seqid ?? 0;
//   const id = item.Itemid ?? item.ItemId ?? 0;
//   console.log("🟢 Editing Item:", item);

//   setEditItemId(seq);
//   setEditItemData(item); 

//   setSelectedItemId(id);
//   setItemSearch(item.ItemNo ?? item.Partno ?? "");
//   setQuantity(String(item.Qty ?? item.qty ?? ""));
//   setRate(String(item.Rate ?? item.rate ?? ""));
//   setDiscountPer(String(getDiscPer(item)));
//   setRemarks(item.Remarks ?? "");

//   setIsModalOpen(true);

//   if (id) {
//     fetchStockData(id);
//   }

//   // ✅ Ensure focus is reset to first field
//   setTimeout(() => itemNameRef.current?.focus(), 50);
// };

//     React.useEffect(() => {
//       if (!isModalOpen || !editItemData || taxTypes.length === 0) return;
  
//       const item = editItemData;
  
     
//       let matchedGroupId = item.TaxGroupId ?? item.Taxgroupid ?? item.TaxGroup ?? null;
  
      
//       if (!matchedGroupId) {
//         const itemTaxTypeText = (item.TaxType || item.TaxtType || item.TaxTypeName || '')?.toString().trim().toLowerCase();
//         if (itemTaxTypeText) {
//           matchedGroupId = (taxTypes.find(t => (t.TaxtType || t.TaxType || '').toString().trim().toLowerCase() === itemTaxTypeText) || {}).GroupId;
//         }
//       }
  
     
//       if (!matchedGroupId) {
//         const itemTaxPer = Number(item.TaxPer ?? item.taxper ?? 0);
//         if (itemTaxPer) {
//           matchedGroupId = (taxTypes.find(t => Number(t.TaxPer) === itemTaxPer) || {}).GroupId;
//         }
//       }
  
     
//       if (!matchedGroupId) {
//         matchedGroupId = taxTypes[0].GroupId;
//         console.log("⚠️ Item has no TaxGroupId or match; defaulting to first:", matchedGroupId);
//       } else {
//         console.log("✅ Restored TaxGroupId from item match:", matchedGroupId);
//       }
  
//       setSelectedTaxGroupId(matchedGroupId);
//     }, [isModalOpen, editItemData, taxTypes]);
  
//     const resetForm = () => {
//       setEditItemId(null);
//       setEditItemData(null);
//       setSelectedItemId('');
//       setQuantity('');
//       setRate('');
//       setDiscountPer('');
//       setItemSearch('');
//       setSelectedTaxGroupId(taxTypes[0]?.GroupId ?? '');
//       setRemarks('');
//     };
    
//   const totalAmount = items.reduce(
//     (sum, item) => sum + ((item.Rate || 0) * (item.Qty || 0) * (1 - (getDiscPer(item) / 100))),
//     0
//   );

//   return (
//     <div className="w-full">
//       <div className="mb-2 text-right">
//          <Button
//           ref={addBtnRef} // ✅ Assign ref
//           onClick={() => { resetForm(); setIsModalOpen(true); }}
//           className="px-2 py-1 bg-blue-500 text-white"
//         >
//           + Add
//         </Button>
//       </div>

//       {/* Modal */}
//       <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}
//         title={editItemId ? "Edit Item" : "Add New Sales Item"}>
//         <div className="grid items-end grid-cols-1 gap-2 sm:grid-cols-5">
        
//           {/* Item Search */}
//           {/* <div className="relative sm:col-span-2">
//             <label className="block text-sm font-medium">Item Name*</label>
//             <input
//               ref={itemNameRef}
//               type="text"
//               value={itemSearch}
//               onChange={e => {
//                 setItemSearch(e.target.value);
//                 setIsItemDropdownOpen(true);
//               }}
//               onFocus={() => {
//                 setIsItemDropdownOpen(true);
//                 if (!itemSearch) setFilteredItems(availableItems);
//               }}
//               onKeyDown={(e) => {
//                 if (isItemDropdownOpen && filteredItems.length > 0) {
//                   if (e.key === "ArrowDown") {
//                     e.preventDefault();
//                     setHighlightIndex((prev) => (prev + 1) % filteredItems.length);
//                   } else if (e.key === "ArrowUp") {
//                     e.preventDefault();
//                     setHighlightIndex((prev) => (prev - 1 + filteredItems.length) % filteredItems.length);
//                   } else if (e.key === "Enter" || e.key === "Tab") {
//                     e.preventDefault();
//                     const chosen =
//                       highlightIndex >= 0 ? filteredItems[highlightIndex] : filteredItems[0];
//                     if (chosen) handleItemSelect(chosen.Itemid);
//                  // Inside handleItemSelect
// setIsDropdownOpen(false);
// setTimeout(() => {
//   if (canFocus("Quantity")) {
//     qtyRef.current?.focus();
//   } else {
//     focusNext("Item Name");
//   }
// }, 50);

//                   }
//                 }
//               }}
//               readOnly={!!editItemId}   //  freeze in edit mode
//               className="block w-full px-2 py-1 mt-1 border rounded"
//               placeholder="Search item by name/code"
//             />
//             {isItemDropdownOpen && filteredItems.length > 0 && (
//               <ul className="absolute z-10 w-full mt-1 bg-white border rounded shadow max-h-60 overflow-y-auto">
//                 {filteredItems.map((item, idx) => (
//                   <li
//                     key={item.ItemId}
//                     onClick={() => {
//                       handleItemSelect(item.ItemId);
//                       setIsItemDropdownOpen(false);
//                       setTimeout(() => qtyRef.current?.focus(), 50);
//                     }}
//                     className={`px-2 py-1 cursor-pointer ${idx === highlightIndex ? "bg-blue-200" : "hover:bg-gray-100"
//                       }`}
//                   >
//                     <strong>{item.ItemNo}</strong> - Rs.{item.Rate} - {getDiscPer(item)}%
//                   </li>
//                 ))}

//               </ul>
//             )}
//           </div> */}

//       {/* Item Search */}
//           <div className="relative sm:col-span-2">
//             <label className="block text-sm font-medium">Item Name*</label>
//             <input
//               ref={itemNameRef}
//               type="text"
//               value={itemSearch}
//               onChange={e => {
//                 setItemSearch(e.target.value);
//                 setIsItemDropdownOpen(true);
//               }}
//               onFocus={() => {
//                 setIsItemDropdownOpen(true);
//                 if (!itemSearch) setFilteredItems(availableItems);
//               }}
//               onKeyDown={(e) => {
//                 if (isItemDropdownOpen && filteredItems.length > 0) {
//                   if (e.key === "ArrowDown") {
//                     e.preventDefault();
//                     setHighlightIndex((prev) => (prev + 1) % filteredItems.length);
//                   } else if (e.key === "ArrowUp") {
//                     e.preventDefault();
//                     setHighlightIndex((prev) => (prev - 1 + filteredItems.length) % filteredItems.length);
//                   } else if (e.key === "Enter" || e.key === "Tab") {
//                     e.preventDefault();
//                     const chosen =
//                       highlightIndex >= 0 ? filteredItems[highlightIndex] : filteredItems[0];
//                     if (chosen) handleItemSelect(chosen.Itemid);
//                     setIsItemDropdownOpen(false);
//                     setTimeout(() => qtyRef.current?.focus(), 50);
//                   }
//                 }
//               }}
//               readOnly={!!editItemId}   //  freeze in edit mode
//               className="block w-full px-2 py-1 mt-1 border rounded"
//               placeholder="Search item by name/code"
//             />
//             {isItemDropdownOpen && filteredItems.length > 0 && (
//               <ul className="absolute z-10 w-full mt-1 bg-white border rounded shadow max-h-60 overflow-y-auto">
//                 {filteredItems.map((item, idx) => (
//                   <li
//                     key={item.ItemId}
//                     onClick={() => {
//                       handleItemSelect(item.ItemId);
//                       setIsItemDropdownOpen(false);
//                       setTimeout(() => qtyRef.current?.focus(), 50);
//                     }}
//                     className={`px-2 py-1 cursor-pointer ${idx === highlightIndex ? "bg-blue-200" : "hover:bg-gray-100"
//                       }`}
//                   >
//                     <strong>{item.ItemNo}</strong> - Rs.{item.Rate} - {getDiscPer(item)}%
//                   </li>
//                 ))}

//               </ul>
//             )}
//           </div>
//           {/* Quantity */}
//           <div>
//             <label className="block text-sm font-medium">Quantity*</label>
// <input
//   id="qtyField"
//   ref={qtyRef}
//   type="number"
//   value={quantity}
//   onChange={e => setQuantity(e.target.value)}
//   onKeyDown={(e) => {
//     if (e.key === "Enter" || e.key === "Tab") {
//       e.preventDefault();
//       focusNext("Quantity");
//     }
//   }}
//   tabIndex={canFocus("Quantity") ? 2 : -1} className="w-full border rounded px-2 py-1 bg-gray-100"
// />

//           </div>

//           {/* Rate */}
//           <div>
//             <label className="block text-sm font-medium">Rate*</label>
// <input
//   id="rateField"
//   type="number"
//   value={rate}
//   onChange={e => setRate(e.target.value)}
//   onKeyDown={(e) => {
//     if (e.key === "Enter" || e.key === "Tab") {
//       e.preventDefault();
//       focusNext("Rate");
//     }
//   }}
//   tabIndex={canFocus("Rate") ? 3 : -1} className="w-full border rounded px-2 py-1 bg-gray-100"
// />

//           </div>

//           {/* Discount % */}
//           <div>
//             <label className="block text-sm font-medium">Disc %</label>

// <input
//   id="discField"
//   type="number"
//   value={discountPer}
//   onChange={e => setDiscountPer(e.target.value)}
//   onKeyDown={(e) => {
//     if (e.key === "Enter" || e.key === "Tab") {
//       e.preventDefault();
//       focusNext("Disc %");
//     }
//   }}
//   tabIndex={canFocus("Disc %") ? 4 : -1} className="w-full border rounded px-2 py-1 bg-gray-100"
// />

//           </div>

//         </div>

//         {/* Discount Amount (calculated) */}
//         <div className="grid items-end grid-cols-1 gap-2 sm:grid-cols-5">
//           <div>
//             <label className="block text-sm font-medium">Disc Amt</label>
//             <input
//               type="number"
//               value={discAmount.toFixed(2)}
//               readOnly
//               tabIndex={-1}   // skip on Tab
//               className="w-full border rounded px-2 py-1 bg-gray-100"
//             />
//           </div>

//         {/* Tax Type */}
// <div className="relative">
//   <label className="block text-sm font-medium">Tax Type*</label>
//   <div
//     id="taxTypeField"
//     tabIndex={5}
//     className="w-full border rounded px-2 py-1 cursor-pointer bg-white"
//     onClick={() => {
//       setIsTaxDropdownOpen(prev => !prev);
//       setTaxHighlightIndex(-1);
//     }}
//     onFocus={() => {
//       setIsTaxDropdownOpen(true);
//       setTaxHighlightIndex(-1);
//     }}
//     onKeyDown={(e) => {
//       if (isTaxDropdownOpen && taxTypes.length > 0) {
//         if (e.key === "ArrowDown") {
//           e.preventDefault();
//           setTaxHighlightIndex((prev) => (prev + 1) % taxTypes.length);
//         } else if (e.key === "ArrowUp") {
//           e.preventDefault();
//           setTaxHighlightIndex((prev) => (prev - 1 + taxTypes.length) % taxTypes.length);
//         } else if (e.key === "Enter" || e.key === "Tab") {
//           e.preventDefault();
//           const chosen =
//             taxHighlightIndex >= 0 ? taxTypes[taxHighlightIndex] : taxTypes[0];
//           if (chosen) setSelectedTaxGroupId(chosen.GroupId);
//           setIsTaxDropdownOpen(false);
//           setTimeout(() => document.getElementById("remarksField")?.focus(), 50);
//         }
//       } else if (e.key === "Enter") {
//         e.preventDefault();
//         setIsTaxDropdownOpen(true);
//       }
//     }}
//   >
//     {(() => {
//       const sel = taxTypes.find(t => t.GroupId == selectedTaxGroupId);
//       return sel ? sel.TaxtType : "-- Select Tax --";
//     })()}
//   </div>

//   {/* ✅ Dropdown list */}
//   {isTaxDropdownOpen && taxTypes.length > 0 && (
//     <ul className="absolute z-10 w-full mt-1 bg-white border rounded shadow max-h-40 overflow-y-auto">
//       {taxTypes.map((tax, idx) => (
//         <li
//           key={tax.GroupId}
//           onClick={() => {
//             setSelectedTaxGroupId(tax.GroupId);
//             setIsTaxDropdownOpen(false);
//             setTimeout(() => document.getElementById("remarksField")?.focus(), 50);
//           }}
//           className={`px-2 py-1 cursor-pointer ${
//             idx === taxHighlightIndex ? "bg-blue-200" : "hover:bg-gray-100"
//           }`}
//         >
//           {tax.TaxtType}
//         </li>
//       ))}
//     </ul>
//   )}
// </div>

//         </div>
//         {/* 🔹 Calculated values */}
//         <div className="sm:col-span-5 mt-3 grid grid-cols-3 gap-2 text-sm">
//           <div className="p-2 border rounded bg-gray-50" tabIndex={-1}>
//             <span className="font-medium">Amount:</span> {subtotal.toFixed(2)}
//           </div>
//           <div className="p-2 border rounded bg-gray-50" tabIndex={-1}>
//             <span className="font-medium">Tax Amt:</span> {taxAmount.toFixed(2)}
//           </div>
//           <div className="p-2 border rounded bg-gray-100 font-bold" tabIndex={-1}>
//             <span className="font-medium">Net Total:</span> {netTotal.toFixed(2)}
//           </div>
//         </div>


//         {/* Remarks */}
//         <div>
//           <label className="block text-sm font-medium">Remarks</label>
//           <input
//             id="remarksField"
//             type="text"
//             value={remarks}
//             onChange={(e) => setRemarks(e.target.value)}
//             onKeyDown={(e) => {
//               if (e.key === "Enter" || e.key === "Tab") {
//                 e.preventDefault();
//                 handleSaveItem(true);   // call directly instead of button click
//               }
//             }}
//             tabIndex={6}
//             className="w-full border rounded px-2 py-1"
//           />

//    </div>

//        {/* Save Button */}
// <div className="mt-4 text-right">
// <Button
//   id="addItemBtn"
//   onClick={() => handleSaveItem(true)}  // stay open for next item
//   disabled={loading || cpid > 0 || vrid > 0}
//   tabIndex={7}
//   className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
// >
//   {loading ? "Saving..." : editItemId ? "Update Item" : "Add Item"}
// </Button>
// </div>
//         {stockData.length > 0 && (
//           <div className="mt-3 sm:col-span-5">
//             <h4 className="font-semibold text-sm mb-2 text-gray-700">
//               Stock Availability
//             </h4>
//             <div className="overflow-hidden rounded-md border border-gray-200 shadow-sm">
//               <table className="w-full text-xs">
//                 <thead className="bg-gray-50 text-gray-600">
//                   <tr>
//                     <th className="px-2 py-1 text-right font-medium">StkQty</th>
//                     <th className="px-2 py-1 text-left font-medium">Godown</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {stockData.map((row, idx) => {
//                     let qtyClass =
//                       row.StkQty < 0
//                         ? "bg-red-100 text-red-700"
//                         : row.StkQty === 0
//                           ? "bg-yellow-100 text-yellow-700"
//                           : "bg-green-100 text-green-700";

//                     return (
//                       <tr
//                         key={idx}
//                         className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
//                       >
//                         <td
//                           className={`px-2 py-1 text-right rounded-sm ${qtyClass}`}
//                         >
//                           {row.StkQty}
//                         </td>
//                         <td className="px-2 py-1">{row.GdName}</td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </table>
//             </div>
//           </div>
//         )}
//       </Modal>

//       {/* Items Table */}
//      <h3 className="mt-4 mb-2 text-lg font-semibold">Sales Order Items</h3>
// <table className="w-full text-sm border">
//   <thead className="bg-gray-50">
//     <tr>
//       <th className="px-2 py-2 text-left">Item</th>
//       <th className="px-2 py-2 text-right">Qty</th>
//       <th className="px-2 py-2 text-right">Rate</th>
//       <th className="px-2 py-2 text-right">Disc %</th>
//       <th className="px-2 py-2 text-right">Disc Amt</th>
//       <th className="px-2 py-2 text-right">Subtotal</th>  
//       <th className="px-2 py-2 text-right">Tax Amt</th>    
//       <th className="px-2 py-2 text-right">Net Total</th> 
//       <th className="px-2 py-2 text-center">Action</th>
//     </tr>
//   </thead>
//   <tbody>
//     {items.map((item, idx) => {
//       const id = item.Seqid ?? item.seqid ?? item.Itemid ?? item.ItemId ?? idx;
//       const itemName = item.Partno ?? item.ItemNo ?? "Unnamed";
//       const qty = Number(item.Qty ?? 0);
//       const rate = Number(item.Rate ?? 0);
//       const disc = getDiscPer(item);

//       // --- calculations ---
//       const subtotal = qty * rate;   // before discount
//       const discAmount = subtotal * (disc / 100);
//       const baseAmount = subtotal - discAmount;
//       const taxRow = taxTypes.find(t => t.GroupId == (item.TaxGroupId ?? 0));
//       const taxPer = taxRow?.TaxPer ?? 0;
//       const taxAmt = item.TaxTotal !== undefined 
//   ? Number(item.TaxTotal) 
//   : baseAmount * (taxPer / 100);
//       const netTotal = baseAmount + taxAmt;

//       return (
//         <tr key={id} className="border-t">
//           <td className="px-2 py-1">{itemName}</td>
//           <td className="px-2 py-1 text-right">{qty}</td>
//           <td className="px-2 py-1 text-right">{rate.toFixed(2)}</td>
//           <td className="px-2 py-1 text-right">{disc}</td>
//           <td className="px-2 py-1 text-right">{discAmount.toFixed(2)}</td>
//           <td className="px-2 py-1 text-right">{baseAmount.toFixed(2)}</td> {/* subtotal */}
//           <td className="px-2 py-1 text-right">{taxAmt.toFixed(2)}</td>
//           <td className="px-2 py-1 text-right">{netTotal.toFixed(2)}</td>
//           <td className="px-2 py-1 text-center space-x-2">
//             <Button
//               onClick={() => handleEditItem(item)}
//               className="px-2 py-1 text-xs bg-yellow-500 hover:bg-yellow-600"
//             >
//               Edit
//             </Button>
//             <Button
//               onClick={() => handleDeleteItem(id)}
//               className="px-2 py-1 text-xs bg-red-500 hover:bg-red-600"
//             >
//               Delete
//             </Button>
//           </td>
//         </tr>
//       );
//     })}
//   </tbody>
//   <tfoot>
//     <tr className="font-bold bg-gray-50 text-xl">
//       <td colSpan="7" className="p-2 text-right  font-bold ">Total Net Amount</td>
//       <td className="p-2 text-right">
//         {items
//           .reduce((sum, item) => {
//             const qty = Number(item.Qty ?? 0);
//             const rate = Number(item.Rate ?? 0);
//             const disc = getDiscPer(item);
//             const subtotal = qty * rate;
//             const baseAmount = subtotal - subtotal * (disc / 100);
//             const taxRow = taxTypes.find(t => t.GroupId == (item.TaxGroupId ?? 0));
//             const taxPer = taxRow?.TaxPer ?? 0;
//          const taxAmt = item.TaxTotal !== undefined 
//   ? Number(item.TaxTotal) 
//   : baseAmount * (taxPer / 100);
//             return sum + baseAmount + taxAmt;
//           }, 0)
//           .toLocaleString("en-IN", { style: "currency", currency: "INR" })}
//       </td>
//       <td></td>
//     </tr>
//   </tfoot>
// </table>

//     </div>
  
//   );
// };

// export default SalesOrderItems;


